import { useEffect, useState } from "react";
import * as React from "react";
import { Box } from "@mui/material";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { useStorageManager } from "../../shared/hooks";
import SkeletonLoader from "../../modules/dashboard/shared/skeleton_loader";
import AIAgentSignalsPanel, {
  AIAgentSignals,
  AIAgentSummary,
} from "./AIAgentSignalsPanel";

const EMPTY_AI_AGENT_SUMMARY: AIAgentSummary = { software_count: 0, signal_count: 0 };
const EMPTY_AI_AGENT_SIGNALS: AIAgentSignals = {
  processes: [],
  vscode_extensions: [],
  jetbrains_plugins: [],
  config_dirs: [],
  browser_extensions: [],
  dns_cache: [],
  builtin_ai_uwp: [],
};

interface AIAgentTabProps {
  ip?: string;
}

// Dedicated "AI Agent" tab in the Discovery details drawer — pulls the same
// WMI_SOFTWARE_BY_IP payload the Software tab uses (ai_agent / ai_agent_signals
// / results[].is_ai_related), but only ever renders the AI panel, since AI
// software itself now lives here instead of the Software tab's grid.
const AIAgentTab: React.FC<AIAgentTabProps> = ({ ip }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<AIAgentSummary>(EMPTY_AI_AGENT_SUMMARY);
  const [signals, setSignals] = useState<AIAgentSignals>(EMPTY_AI_AGENT_SIGNALS);
  const [aiSoftware, setAiSoftware] = useState<{ name: string; version: string }[]>([]);

  useEffect(() => {
    if (!ip) {
      setIsLoading(false);
      return;
    }

    const fetchAIAgentData = async () => {
      setIsLoading(true);
      try {
        const { getFromLocalStorage } = useStorageManager();
        const userDetails = JSON.parse(getFromLocalStorage("userDetails") || "{}");
        const organizationId = userDetails?.organization_id;

        const params: any = { ip };
        if (organizationId) params.organization_id = organizationId;

        const response = await axiosInstance.get<any>(END_POINTS.WMI_SOFTWARE_BY_IP, { params });

        if (response.status === 200) {
          setSummary(response.data?.ai_agent || EMPTY_AI_AGENT_SUMMARY);
          setSignals(response.data?.ai_agent_signals || EMPTY_AI_AGENT_SIGNALS);

          const rawGrouped = response.data?.grouped_results || response.data?.data?.grouped_results || [];
          const rawResults =
            response.data?.results ||
            response.data?.data?.results ||
            response.data?.software ||
            response.data?.data?.software ||
            (Array.isArray(response.data) ? response.data : []);
          const flatResults = rawGrouped.length > 0 ? rawGrouped.flatMap((g: any) => g.softwares || []) : rawResults;

          setAiSoftware(
            (flatResults || [])
              .filter((item: any) => item.is_ai_related)
              .map((item: any) => ({ name: item.name || "", version: item.version || "" })),
          );
        }
      } catch (error) {
        console.error("Error fetching AI agent data:", error);
        setSummary(EMPTY_AI_AGENT_SUMMARY);
        setSignals(EMPTY_AI_AGENT_SIGNALS);
        setAiSoftware([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAIAgentData();
  }, [ip]);

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <SkeletonLoader count={5} styles={{ height: 30, width: "100%" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <AIAgentSignalsPanel summary={summary} signals={signals} aiSoftware={aiSoftware} />
    </Box>
  );
};

export default AIAgentTab;
